// Welcome Kit Quiz — Villas Pura Vida
// 5-question quiz to discover the guest's vacation profile + personalized welcome kit
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowRight, ArrowLeft, Share2, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import Booking from "@/components/Booking";

// Each option carries weights for the 4 profiles
const QUESTIONS = [
  {
    id: "morning",
    es: { q: "¿Cómo te imaginas tu mañana perfecta en la playa?", o: [
      { text: "Café tranquilo viendo el amanecer", w: { contemplativo: 3, romantico: 1 } },
      { text: "Trotar en la playa o estirar en la arena", w: { contemplativo: 2, aventurero: 1 } },
      { text: "Desayuno fuerte con la familia", w: { festivo: 2, aventurero: 1 } },
      { text: "Dormir hasta tarde sin alarmas", w: { contemplativo: 2, romantico: 1 } },
    ]},
    en: { q: "How do you picture your perfect beach morning?", o: [
      { text: "Quiet coffee watching the sunrise", w: { contemplativo: 3, romantico: 1 } },
      { text: "Jog on the beach or stretch in the sand", w: { contemplativo: 2, aventurero: 1 } },
      { text: "Big breakfast with the family", w: { festivo: 2, aventurero: 1 } },
      { text: "Sleep in without alarms", w: { contemplativo: 2, romantico: 1 } },
    ]},
  },
  {
    id: "company",
    es: { q: "¿Con quién vienes?", o: [
      { text: "En pareja", w: { romantico: 3 } },
      { text: "Familia con niños", w: { aventurero: 2, festivo: 2 } },
      { text: "Grupo de amigos", w: { festivo: 3, aventurero: 1 } },
      { text: "Vengo solo-a a desconectar", w: { contemplativo: 3 } },
    ]},
    en: { q: "Who are you traveling with?", o: [
      { text: "As a couple", w: { romantico: 3 } },
      { text: "Family with kids", w: { aventurero: 2, festivo: 2 } },
      { text: "Group of friends", w: { festivo: 3, aventurero: 1 } },
      { text: "Solo, to disconnect", w: { contemplativo: 3 } },
    ]},
  },
  {
    id: "vibe",
    es: { q: "¿Cuál es tu vibra ideal?", o: [
      { text: "Relax total — leer, hamaca, nada de prisa", w: { contemplativo: 3, romantico: 1 } },
      { text: "Aventura — explorar la zona", w: { aventurero: 3 } },
      { text: "Fiesta tranquila — música, comida, risas", w: { festivo: 3 } },
      { text: "Desconexión digital absoluta", w: { contemplativo: 3 } },
    ]},
    en: { q: "What's your ideal vibe?", o: [
      { text: "Total relax — book, hammock, no rush", w: { contemplativo: 3, romantico: 1 } },
      { text: "Adventure — explore the area", w: { aventurero: 3 } },
      { text: "Chill party — music, food, laughs", w: { festivo: 3 } },
      { text: "Absolute digital detox", w: { contemplativo: 3 } },
    ]},
  },
  {
    id: "moment",
    es: { q: "¿Qué te emociona más?", o: [
      { text: "Atardecer con copa de vino", w: { romantico: 3 } },
      { text: "Fogata con carnita asada al mar", w: { festivo: 3, aventurero: 1 } },
      { text: "Ver cangrejos azules de noche", w: { aventurero: 3, contemplativo: 1 } },
      { text: "Caminar descalzo por horas", w: { contemplativo: 3 } },
    ]},
    en: { q: "What excites you most?", o: [
      { text: "Sunset with a glass of wine", w: { romantico: 3 } },
      { text: "Bonfire with grilled meat by the sea", w: { festivo: 3, aventurero: 1 } },
      { text: "Spotting blue crabs at night", w: { aventurero: 3, contemplativo: 1 } },
      { text: "Walking barefoot for hours", w: { contemplativo: 3 } },
    ]},
  },
  {
    id: "kit",
    es: { q: "¿Qué prefieres encontrar al llegar?", o: [
      { text: "Frutas frescas y aguas naturales", w: { contemplativo: 2, romantico: 1 } },
      { text: "Botana salada, papas y cervezas", w: { festivo: 3 } },
      { text: "Smoothies, jugos energéticos y snacks", w: { aventurero: 3 } },
      { text: "Algo dulce — chocolate, vino, queso", w: { romantico: 3 } },
    ]},
    en: { q: "What do you prefer to find on arrival?", o: [
      { text: "Fresh fruit and natural waters", w: { contemplativo: 2, romantico: 1 } },
      { text: "Salty snacks, chips, beers", w: { festivo: 3 } },
      { text: "Smoothies, energy juices, snacks", w: { aventurero: 3 } },
      { text: "Something sweet — chocolate, wine, cheese", w: { romantico: 3 } },
    ]},
  },
];

const PROFILES = {
  contemplativo: {
    emoji: "🌅",
    color: "#81B29A",
    bg: "#E4F1EA",
    es: {
      name: "El Contemplativo",
      tagline: "Vienes a respirar profundo, no a llenar agenda.",
      desc: "Buscas paz, silencio y reconectar contigo. Los amaneceres descalzos y el sonido del mar son tu medicina. Te recomiendo llevar: café del bueno, frutas frescas de temporada, agua mineral, un libro favorito y aloe vera para después del sol.",
    },
    en: {
      name: "The Contemplative",
      tagline: "You come to breathe deeply, not fill a schedule.",
      desc: "You seek peace, silence and reconnection. Barefoot sunrises and the sound of the sea are your medicine. I'd recommend bringing: a good coffee, fresh seasonal fruit, mineral water, your favorite book and aloe vera for after-sun.",
    },
  },
  aventurero: {
    emoji: "🏖️",
    color: "#E07A5F",
    bg: "#FCEFE6",
    es: {
      name: "El Aventurero",
      tagline: "Vienes con energía y ganas de explorar todo.",
      desc: "Te emociona caminar la playa, descubrir cangrejos, buscar conchas y vivir la zona al máximo. Te recomiendo llevar: bloqueador solar potente, lentes de snorkel, una hielera con bebidas frías, snacks proteicos y trajes de baño extra.",
    },
    en: {
      name: "The Adventurer",
      tagline: "You come with energy and curiosity.",
      desc: "Walking the beach, spotting crabs, hunting shells and living the area to the fullest is your thing. I'd recommend bringing: strong sunblock, snorkel goggles, a cooler with cold drinks, protein snacks and extra swimsuits.",
    },
  },
  romantico: {
    emoji: "🍷",
    color: "#B3613C",
    bg: "#F5E2D2",
    es: {
      name: "El Romántico",
      tagline: "Vienes a crear momentos que se sienten en el alma.",
      desc: "Buscas atardeceres compartidos, vino al amanecer y silencios que dicen todo. Te recomiendo llevar: vino tinto, una tabla de quesos, chocolates artesanales, velas o lucecitas para el atardecer y una bocina para música suave.",
    },
    en: {
      name: "The Romantic",
      tagline: "You come to create soul-deep moments.",
      desc: "You seek shared sunsets, wine at dawn and silences that say everything. I'd recommend bringing: red wine, a cheese board, artisan chocolates, candles or string lights for sunset and a speaker for soft music.",
    },
  },
  festivo: {
    emoji: "🎉",
    color: "#F2CC8F",
    bg: "#FAF1D8",
    es: {
      name: "El Festivo",
      tagline: "Vienes con tu tribu y la vibra alta.",
      desc: "Música, comida, risas y carnita asada al atardecer. Te recomiendo llevar: carne para asar, nopalitos, cervezas frías, salsas con limón y una bocina para la fiesta.",
    },
    en: {
      name: "The Festive",
      tagline: "You come with your tribe and high vibes.",
      desc: "Music, food, laughs and grilled meat at sunset. I'd recommend bringing: meat for the grill, nopalitos (cactus), cold beers, fresh salsas with limes and a speaker for the music.",
    },
  },
};

export default function Quiz() {
  const { lang } = useLang();
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = email, N+2 = result
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlock, setUnlock] = useState(null); // { token, expiresAt }
  // Session id for funnel tracking
  const [sessionId] = useState(() => {
    try {
      const id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
      return id;
    } catch { return Math.random().toString(36).slice(2); }
  });

  const track = (event, extra = {}) => {
    // fire-and-forget
    api.post("/quiz/track", { event, session_id: sessionId, lang, ...extra }).catch(() => {});
  };

  const totalQuestions = QUESTIONS.length;
  const isIntro = step === 0;
  const isQuestion = step >= 1 && step <= totalQuestions;
  // Email step is no longer required — kept in state for backward compat but never rendered
  const isEmailStep = false;
  const isResult = step === totalQuestions + 2;

  // Auto-submit anonymously when reaching end of questions
  useEffect(() => {
    if (step === totalQuestions + 1 && !result && !loading) {
      submitAnonymous();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submitAnonymous = async () => {
    setLoading(true);
    const profile = computeProfile();
    try {
      const { data } = await api.post("/quiz/submit", {
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, String(v)])),
        profile,
        lang,
        session_id: sessionId,
      });
      setResult(profile);
      if (data.unlock_token && data.expires_at) {
        setUnlock({ token: data.unlock_token, expiresAt: data.expires_at });
      }
      setStep(totalQuestions + 2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "es" ? "Error" : "Error"));
    } finally {
      setLoading(false);
    }
  };

  const currentQ = isQuestion ? QUESTIONS[step - 1] : null;
  const currentQContent = currentQ ? currentQ[lang] : null;

  const selectAnswer = (qid, optionIdx) => {
    setAnswers((prev) => ({ ...prev, [qid]: optionIdx }));
    track("answer", { question_id: qid });
    // Auto-advance after 300ms
    setTimeout(() => setStep((s) => s + 1), 280);
  };

  const computeProfile = () => {
    const scores = { contemplativo: 0, aventurero: 0, romantico: 0, festivo: 0 };
    QUESTIONS.forEach((q) => {
      const idx = answers[q.id];
      if (idx === undefined) return;
      const opt = q[lang].o[idx];
      Object.entries(opt.w || {}).forEach(([k, v]) => { scores[k] = (scores[k] || 0) + v; });
    });
    let best = "contemplativo";
    let max = -1;
    Object.entries(scores).forEach(([k, v]) => { if (v > max) { max = v; best = k; } });
    return best;
  };

  const submitEmail = async () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error(lang === "es" ? "Email inválido" : "Invalid email");
      return;
    }
    setLoading(true);
    const profile = computeProfile();
    try {
      const { data } = await api.post("/quiz/submit", {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, String(v)])),
        profile,
        lang,
      });
      setResult(profile);
      if (data.unlock_token && data.expires_at) {
        setUnlock({ token: data.unlock_token, expiresAt: data.expires_at });
      }
      setStep(totalQuestions + 2);
      toast.success(lang === "es" ? "¡Tu resultado está listo! Reserva ahora con 10% off." : "Your result is ready! Book now with 10% off.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "es" ? "Error" : "Error"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0); setAnswers({}); setEmail(""); setName(""); setResult(null); setUnlock(null);
  };

  const profileInfo = result ? PROFILES[result] : null;
  const profileContent = profileInfo ? profileInfo[lang] : null;

  // Countdown for result page (separate from Booking's internal one)
  const [resultNow, setResultNow] = useState(Date.now());
  useEffect(() => {
    if (!unlock?.expiresAt) return;
    const id = setInterval(() => setResultNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [unlock?.expiresAt]);
  const secondsLeft = unlock?.expiresAt ? Math.max(0, Math.floor((new Date(unlock.expiresAt).getTime() - resultNow) / 1000)) : 0;
  const offerActive = !!unlock?.token && secondsLeft > 0;
  const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const scrollToBooking = () => {
    const el = document.querySelector('[data-testid="quiz-embedded-booking"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shareText = profileContent ? (lang === "es"
    ? `Hice el quiz de Villas Pura Vida y soy ${profileInfo.emoji} ${profileContent.name}! ${profileContent.tagline} Descubre tu perfil en puravidatuxpan.com/quiz #VillasPuraVida #PuraVida`
    : `I took the Villas Pura Vida quiz and I'm ${profileInfo.emoji} ${profileContent.name}! Find your profile at puravidatuxpan.com/quiz #VillasPuraVida`)
    : "";

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/quiz` : "";

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="quiz-page">
      <PageSEO
        title="Welcome Kit Quiz · Descubre tu perfil de viaje · Villas Pura Vida"
        description="Responde 5 preguntas rápidas y descubre tu perfil de huésped: contemplativo, aventurero, romántico o festivo. Desbloquea un welcome kit personalizado para tu estancia en Villas Pura Vida."
        path="/quiz"
      />      <style>{`
        @keyframes megaShimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
      <Header variant="home" />

      {/* Sticky countdown bar (only when offer active) */}
      {isResult && offerActive && (
        <div data-testid="quiz-sticky-bar" className="fixed top-16 md:top-20 left-0 right-0 z-40">
          <button
            onClick={scrollToBooking}
            className="w-full text-white px-4 py-3 text-sm md:text-base font-semibold flex items-center justify-center gap-3 hover:brightness-110 transition-all"
            style={{
              backgroundImage: "linear-gradient(90deg, #DC2626, #EF4444, #DC2626)",
              backgroundSize: "200% 100%",
              animation: "megaShimmer 4s linear infinite",
            }}
          >
            <span className="text-xl">🔥</span>
            <span className="hidden sm:inline">{lang === "es" ? "Reserva ahora con" : "Book now with"}</span>
            <span className="font-black">10% OFF</span>
            <span className="opacity-90">·</span>
            <span className="opacity-90">{lang === "es" ? "Expira en" : "Expires in"}</span>
            <span className="font-mono font-black tabular-nums text-base md:text-lg bg-white/20 px-2 py-0.5 rounded">{fmtCountdown(secondsLeft)}</span>
            <ArrowRight className="w-4 h-4 hidden sm:inline" />
          </button>
        </div>
      )}

      <main className={`${isResult && offerActive ? "pt-44 md:pt-52" : "pt-32 md:pt-40"} pb-24`}>
        <div className="max-w-3xl mx-auto px-6">
          {/* Progress bar */}
          {isQuestion && (
            <div className="mb-10" data-testid="quiz-progress">
              <div className="flex justify-between text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">
                <span>{lang === "es" ? "Pregunta" : "Question"} {step}/{totalQuestions}</span>
                <button onClick={reset} className="hover:text-stone-900 transition-colors">{lang === "es" ? "Reiniciar" : "Restart"}</button>
              </div>
              <div className="h-1 bg-stone-200">
                <div className="h-1 bg-[#E07A5F] transition-all duration-500" style={{ width: `${(step / totalQuestions) * 100}%` }} />
              </div>
            </div>
          )}

          {/* INTRO */}
          {isIntro && (
            <div className="text-center" data-testid="quiz-intro">
              {/* Prominent 10% OFF badge — no registration required */}
              <div className="inline-flex flex-col items-center mb-8" data-testid="quiz-offer-badge">
                <div className="relative inline-block">
                  <span className="absolute -top-2 -right-2 bg-[#E07A5F] text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full shadow-md rotate-6">
                    {lang === "es" ? "Sin registro" : "No signup"}
                  </span>
                  <div className="bg-gradient-to-br from-[#E07A5F] via-[#DC7457] to-[#B85F49] text-white px-8 py-3 rounded-none shadow-lg">
                    <span className="font-serif-display italic text-4xl md:text-5xl leading-none">10%</span>
                    <span className="font-sans text-lg uppercase tracking-[0.25em] ml-2 font-semibold">OFF</span>
                  </div>
                </div>
                <p className="mt-4 font-serif-display italic text-lg md:text-xl text-stone-600 leading-snug max-w-md">
                  {lang === "es"
                    ? "Solo por el placer de escuchar el mar."
                    : "Just for the pleasure of listening to the sea."}
                </p>
              </div>

              <p className="text-eyebrow text-[#E07A5F] mb-4 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {lang === "es" ? "Quiz · 2 min" : "Quiz · 2 min"}
              </p>
              <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-stone-900 mb-6">
                {lang === "es" ? <>¿Qué tipo de <span className="italic text-[#E07A5F]">vacacionista</span> eres?</> : <>What kind of <span className="italic text-[#E07A5F]">vacationer</span> are you?</>}
              </h1>
              <p className="text-stone-700 text-lg md:text-xl leading-relaxed mb-10 max-w-xl mx-auto">
                {lang === "es"
                  ? "5 preguntas rápidas para descubrir tu perfil Pura Vida y desbloquear tu código de descuento."
                  : "5 quick questions to discover your Pura Vida profile and unlock your discount code."}
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-10 text-3xl md:text-5xl">
                {Object.values(PROFILES).map((p) => (
                  <span key={p.emoji} title={p.es.name} className="grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-500">{p.emoji}</span>
                ))}
              </div>
              <Button onClick={() => { track("start"); setStep(1); }} data-testid="quiz-start-btn" className="bg-stone-900 hover:bg-[#E07A5F] text-white rounded-none px-10 py-7 text-sm uppercase tracking-[0.18em] font-semibold transition-colors">
                {lang === "es" ? "Empezar quiz" : "Start quiz"} <ArrowRight className="ml-3 w-4 h-4" />
              </Button>
              <p className="mt-8 text-xs text-stone-400">{lang === "es" ? "Sin registros. Sin email. Tu 10% se aplica automático al reservar directo." : "No signup. No email. Your 10% applies automatically when you book direct."}</p>
            </div>
          )}

          {/* QUESTIONS */}
          {isQuestion && currentQContent && (
            <div data-testid={`quiz-question-${step}`}>
              <h2 className="font-serif-display text-3xl md:text-5xl leading-tight tracking-tight text-stone-900 mb-10">
                {currentQContent.q}
              </h2>
              <div className="space-y-3">
                {currentQContent.o.map((opt, i) => {
                  const selected = answers[currentQ.id] === i;
                  return (
                    <button
                      key={i}
                      onClick={() => selectAnswer(currentQ.id, i)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className={`w-full text-left px-6 py-5 border-2 transition-all duration-200 group ${
                        selected
                          ? "border-[#E07A5F] bg-[#FCEFE6] text-stone-900"
                          : "border-stone-200 bg-white text-stone-700 hover:border-stone-900 hover:translate-x-1"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-lg md:text-xl">{opt.text}</span>
                        <ArrowRight className={`w-5 h-5 transition-opacity ${selected ? "opacity-100 text-[#E07A5F]" : "opacity-0 group-hover:opacity-100 text-stone-900"}`} />
                      </div>
                    </button>
                  );
                })}
              </div>
              {step > 1 && (
                <button onClick={() => setStep(step - 1)} data-testid="quiz-back-btn" className="mt-10 inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#E07A5F] transition-colors">
                  <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Anterior" : "Previous"}
                </button>
              )}
            </div>
          )}

          {/* AUTO-SUBMIT loading (between last question and result) */}
          {step === totalQuestions + 1 && !result && (
            <div className="text-center py-16" data-testid="quiz-computing">
              <div className="inline-flex items-center gap-3 text-stone-500">
                <div className="w-6 h-6 border-2 border-[#E07A5F] border-t-transparent rounded-full animate-spin" />
                <span className="font-serif-display italic text-2xl">
                  {lang === "es" ? "Calculando tu perfil…" : "Computing your profile…"}
                </span>
              </div>
            </div>
          )}

          {/* EMAIL STEP (deprecated — kept for backward compat, never rendered) */}
          {isEmailStep && (
            <div className="text-center" data-testid="quiz-email-step">
              <p className="text-eyebrow text-[#E07A5F] mb-4">{lang === "es" ? "Último paso" : "Last step"}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight text-stone-900 mb-4">
                {lang === "es" ? "Descubre tu perfil" : "Discover your profile"}
              </h2>
              <p className="text-stone-700 text-lg leading-relaxed mb-10 max-w-md mx-auto">
                {lang === "es"
                  ? "Te enviamos tu resultado y un código de descuento exclusivo a tu correo."
                  : "We'll email you your result plus an exclusive discount code."}
              </p>
              <div className="max-w-md mx-auto space-y-3">
                <Input
                  placeholder={lang === "es" ? "Tu nombre" : "Your name"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="quiz-name-input"
                  className="rounded-none border-stone-300 px-4 py-6 text-base text-center"
                />
                <Input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="quiz-email-input"
                  className="rounded-none border-stone-300 px-4 py-6 text-base text-center"
                />
                <Button onClick={submitEmail} disabled={loading} data-testid="quiz-submit-btn" className="w-full bg-[#E07A5F] hover:bg-stone-900 text-white rounded-none py-7 text-sm uppercase tracking-[0.18em] font-semibold transition-colors">
                  {loading ? (lang === "es" ? "Calculando..." : "Computing...") : (lang === "es" ? "Ver mi resultado" : "See my result")} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-xs text-stone-400 pt-2">{lang === "es" ? "Sin spam. Promesa." : "No spam. Promise."}</p>
              </div>
            </div>
          )}

          {/* RESULT */}
          {isResult && profileContent && (
            <div className="text-center" data-testid="quiz-result">
              <p className="text-eyebrow text-stone-500 mb-4">{lang === "es" ? "Tu perfil Pura Vida es" : "Your Pura Vida profile is"}</p>

              <div className="inline-block mb-10" style={{ background: profileInfo.bg, padding: "48px 48px 40px" }}>
                <div className="text-7xl md:text-8xl mb-4 leading-none">{profileInfo.emoji}</div>
                <h1 className="font-serif-display text-4xl md:text-6xl leading-tight tracking-tight" style={{ color: profileInfo.color }}>
                  {profileContent.name}
                </h1>
              </div>

              <p className="font-serif-display italic text-2xl md:text-3xl text-stone-900 mb-6 max-w-2xl mx-auto leading-tight">
                &ldquo;{profileContent.tagline}&rdquo;
              </p>
              <p className="text-stone-700 text-lg leading-relaxed mb-12 max-w-xl mx-auto">{profileContent.desc}</p>

              {/* MEGA PROMO BANNER */}
              {offerActive && (
                <div data-testid="quiz-mega-promo" className="mb-12 relative overflow-hidden">
                  <style>{`
                    @keyframes megaPulse {
                      0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.55), 0 8px 30px -10px rgba(0,0,0,0.25); }
                      50% { box-shadow: 0 0 0 18px rgba(220, 38, 38, 0), 0 8px 30px -10px rgba(0,0,0,0.25); }
                    }
                    @keyframes megaShimmer {
                      0% { background-position: 0% 50%; }
                      100% { background-position: 200% 50%; }
                    }
                    @keyframes megaTick {
                      0%, 100% { transform: scale(1); }
                      50% { transform: scale(1.06); }
                    }
                    @keyframes megaFlame {
                      0%, 100% { transform: scale(1) rotate(-3deg); }
                      50% { transform: scale(1.15) rotate(3deg); }
                    }
                  `}</style>
                  <div
                    className="px-6 py-8 md:py-10 text-white relative"
                    style={{
                      backgroundImage: "linear-gradient(110deg, #DC2626 0%, #B91C1C 35%, #EF4444 50%, #DC2626 65%, #B91C1C 100%)",
                      backgroundSize: "200% 100%",
                      animation: "megaPulse 2s ease-in-out infinite, megaShimmer 5s linear infinite",
                    }}
                  >
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex items-center gap-3">
                        <span className="text-4xl md:text-5xl" style={{ animation: "megaFlame 1s ease-in-out infinite" }}>🔥</span>
                        <p className="text-[11px] md:text-xs uppercase tracking-[0.35em] font-black">{lang === "es" ? "Tu Oferta Exclusiva" : "Your Exclusive Offer"}</p>
                        <span className="text-4xl md:text-5xl" style={{ animation: "megaFlame 1s ease-in-out infinite" }}>🔥</span>
                      </div>
                      <p className="font-serif-display text-6xl md:text-8xl font-bold leading-none drop-shadow-lg">
                        10% <span className="italic font-light">off</span>
                      </p>
                      <p className="text-sm md:text-base font-medium opacity-95 max-w-md">
                        {lang === "es" ? "vs. precio Airbnb · si reservas en los próximos:" : "vs. Airbnb price · if you book in the next:"}
                      </p>
                      <p
                        className="font-mono text-5xl md:text-7xl font-black tabular-nums tracking-tight drop-shadow-lg"
                        style={{ animation: "megaTick 1s ease-in-out infinite" }}
                        data-testid="mega-countdown"
                      >
                        {fmtCountdown(secondsLeft)}
                      </p>
                      <button
                        onClick={scrollToBooking}
                        data-testid="mega-book-cta"
                        className="mt-3 inline-flex items-center gap-2 bg-white text-red-700 hover:bg-stone-900 hover:text-white px-10 py-5 text-sm md:text-base uppercase tracking-[0.18em] font-black transition-colors"
                      >
                        {lang === "es" ? "Reservar ahora con 10% off" : "Book now with 10% off"} <ArrowRight className="w-5 h-5" />
                      </button>
                      <p className="text-[11px] uppercase tracking-[0.25em] opacity-80 mt-1">
                        {lang === "es" ? "Solo por reservar directo · No por Airbnb" : "Direct booking only · Not on Airbnb"}
                      </p>
                    </div>
                  </div>
                </div>
              )}



              {/* Share */}
              <div className="mb-10" data-testid="quiz-share">
                <p className="text-eyebrow text-stone-500 mb-4 inline-flex items-center gap-2"><Share2 className="w-3 h-3" /> {lang === "es" ? "Compártelo" : "Share it"}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="share-whatsapp"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[#25D366] hover:bg-[#1FB855] text-white text-sm uppercase tracking-[0.15em] font-semibold transition-colors"
                  >
                    <i className="fa-brands fa-whatsapp text-lg" /> WhatsApp
                  </a>
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareText + " " + shareUrl); toast.success(lang === "es" ? "Copiado" : "Copied"); }}
                    data-testid="share-copy"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-stone-900 hover:bg-stone-700 text-white text-sm uppercase tracking-[0.15em] font-semibold transition-colors"
                  >
                    <i className="fa-solid fa-link" /> {lang === "es" ? "Copiar enlace" : "Copy link"}
                  </button>
                  <a
                    href={`https://www.instagram.com/`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => { navigator.clipboard.writeText(shareText + " " + shareUrl); toast.success(lang === "es" ? "Texto copiado — pégalo en tu Story" : "Text copied — paste in your Story"); }}
                    data-testid="share-instagram"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white text-sm uppercase tracking-[0.15em] font-semibold transition-opacity hover:opacity-90"
                  >
                    <i className="fa-brands fa-instagram text-lg" /> Instagram
                  </a>
                </div>
              </div>

              {/* CTA */}
              <div className="border-t border-stone-200 pt-10">
                <p className="text-stone-700 text-lg mb-2">{lang === "es" ? "Reserva directo y aprovecha tu descuento exclusivo." : "Book direct and enjoy your exclusive discount."}</p>
                <p className="text-xs text-[#3F6A53] font-semibold uppercase tracking-[0.15em] mb-6">
                  ✦ {lang === "es" ? "10% off exclusivo · solo por 10 minutos" : "10% off exclusive · only 10 minutes"} ✦
                </p>
                <p className="text-xs text-stone-500">
                  <button onClick={reset} className="hover:text-stone-900 transition-colors underline">{lang === "es" ? "Hacer el quiz otra vez" : "Take the quiz again"}</button>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Embedded booking widget with quiz unlock */}
        {isResult && unlock && (
          <div className="mt-10" data-testid="quiz-embedded-booking">
            <Booking allowPicker={true} quizUnlock={unlock} defaultKit={true} />
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
